import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import "./multiRangeSlider.scss";

const MultiRangeSlider = ({ min, max, onChange, initialMin, initialMax }) => {
  const [minVal, setMinVal] = useState(initialMin || min);
  const [maxVal, setMaxVal] = useState(initialMax || max);
  // Safe fallback for currency symbol (avoid undefined variable)
  const currencySymbol = (typeof window !== 'undefined' && window.store_currency_symbol) || '$';
  const [isDragging, setIsDragging] = useState(false);
  const minValRef = useRef(initialMin || min);
  const maxValRef = useRef(initialMax || max);
  const range = useRef(null);

  // Convert to percentage
  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  // Set width of the range to decrease from the left side
  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, getPercent]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    const minPercent = getPercent(minValRef.current);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, getPercent]);

  // // Get min and max values when their state changes
  useEffect(() => {
    // Only call onChange when not dragging
    if (!isDragging) {
      onChange({ min: minVal, max: maxVal });
    }
  }, [minVal, maxVal, onChange, isDragging]);

  // Update values when props change
  useEffect(() => {
    if (initialMin !== undefined && initialMin !== minVal) {
      setMinVal(initialMin);
      minValRef.current = initialMin;
    }
  }, [initialMin]);

  useEffect(() => {
    if (initialMax !== undefined && initialMax !== maxVal) {
      setMaxVal(initialMax);
      maxValRef.current = initialMax;
    }
  }, [initialMax]);

  return (
    <div className="slider-container h-full w-full mb-[40px]">
      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => {
          setIsDragging(false);
          onChange({ min: minVal, max: maxVal });
        }}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => {
          setIsDragging(false);
          onChange({ min: minVal, max: maxVal });
        }}
        onChange={(event) => {
          const value = Math.min(Number(event.target.value), maxVal - 1);
          setMinVal(value);
          minValRef.current = value;
        }}
        className="thumb thumb--left"
        style={{ zIndex: minVal > max - 100 && "5" }}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => {
          setIsDragging(false);
          onChange({ min: minVal, max: maxVal });
        }}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => {
          setIsDragging(false);
          onChange({ min: minVal, max: maxVal });
        }}
        onChange={(event) => {
          const value = Math.max(Number(event.target.value), minVal + 1);
          setMaxVal(value);
          maxValRef.current = value;
        }}
        className="thumb thumb--right"
      />

      <div className="slider">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
        <div className="slider__left-value b8--medium">{currencySymbol}{minVal}</div>
        <div className="slider__right-value b8--medium">{currencySymbol}{maxVal}</div>
      </div>
    </div>
  );
};

MultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  initialMin: PropTypes.number,
  initialMax: PropTypes.number
};

export default MultiRangeSlider;
